import { useMutation, useQuery } from '@tanstack/react-query';
import { components } from '@/types/be-schema';
import { getUserAuthHeader } from '../../lib/schema/user/client-fetch';

export function useGetSavedVehicleInfo(
  vin: string | null,
  hashed_vin: string | null,
  shouldFetch = true,
  cancelOffer?: boolean
) {
  return useQuery<components['schemas']['PrivateVehicleData'] | null>({
    queryKey: ['savedVehicleInfo', vin, hashed_vin, cancelOffer],
    throwOnError: false,
    refetchOnWindowFocus: false,
    retry: false,
    enabled: shouldFetch,
    queryFn: async () => {
      try {
        const authHeader = await getUserAuthHeader();
        if (authHeader) {
          const response = await fetch(
            `/api/sell_my_car/save_vehicle_info/?vin=${vin}&hashed_vin=${hashed_vin}`,
            {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                Authorization: authHeader,
              },
            }
          );
          if (!response.ok) {
            return null;
          }
          const savedVehicleInfo = await response.json();
          return savedVehicleInfo;
        }
        return null;
      } catch (error) {
        throw new Error('Failed to fetch saved vehicle info');
      }
    },
  });
}

export function useSaveVehicleInfoMutation() {
  return useMutation({
    mutationFn: async (body: components['schemas']['PrivateVehicleData']) => {
      try {
        const authHeader = await getUserAuthHeader();
        if (authHeader) {
          const response = await fetch(`/api/sell_my_car/save_vehicle_info`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: authHeader,
            },
            body: JSON.stringify(body),
          });
          if (!response.ok) {
            throw new Error('unable to save user vehicle info');
          }
          const savedVin = await response.json();
          return savedVin;
        }
        return null;
      } catch (error) {
        throw new Error('Internal Server Error');
      }
    },
  });
}

export function useUpdateVehicleInfoMutation(vin: string) {
  return useMutation({
    mutationFn: async (body: components['schemas']['PrivateVehicleData']) => {
      try {
        const authHeader = await getUserAuthHeader();
        if (authHeader) {
          const response = await fetch(
            `/api/sell_my_car/save_vehicle_info/?vin=${vin}`,
            {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
                Authorization: authHeader,
              },
              body: JSON.stringify(body),
            }
          );
          if (!response.ok) {
            throw new Error('unable to update user vehicle info');
          }
          return true;
        }
        return false;
      } catch (error) {
        throw new Error('Internal Server Error');
      }
    },
  });
}

export function useRemoveVehicleInfoMutation() {
  return useMutation({
    mutationFn: async (vin: string) => {
      try {
        const authHeader = await getUserAuthHeader();
        if (authHeader) {
          const response = await fetch(
            `/api/sell_my_car/save_vehicle_info?vin=${vin}`,
            {
              method: 'DELETE',
              headers: {
                'Content-Type': 'application/json',
                Authorization: authHeader,
              },
            }
          );
          if (!response.ok) {
            throw new Error('Failed to remove user vehicle info');
          }
        }
      } catch (error) {
        throw new Error('Internal Server Error');
      }
    },
  });
}

export function useSendLeadEmailMutation(vin: string, email?: string) {
  return useMutation({
    mutationFn: async () => {
      try {
        const authHeader = await getUserAuthHeader();
        if (authHeader) {
          const response = await fetch(
            `/api/sell_my_car/send_lead_email?vin=${vin}&email=${email}`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: authHeader,
              },
            }
          );
          if (!response.ok) {
            throw new Error('Failed to send lead email');
          }
        }
      } catch (error) {
        throw new Error('Internal Server Error');
      }
    },
  });
}
